/**
 * Constants for Azure Referral Form
 * Includes Solution Areas, Solution Plays, Help Types, and other form options
 */
import {
  AZURE_DEAL_TYPE_OPTIONS,
  AZURE_REFERRAL_STATUS,
  AZURE_REFERRAL_SUBSTATUS,
  TERMINAL_AZURE_REFERRAL_SUBSTATUSES,
  matchesReferralStatus,
  matchesReferralSubstatus,
  type ReferralStatus,
  type ReferralSubstatus,
} from '../../../../oppsync/modules/CoSell/AzureReferralForm/azureReferralSchema'

import { SelectorOptions } from '@labrav/react-components/lib/@types/types/forms'
import { StatusChipBg, StatusChipText } from '@labrav/react-components'

export const SOLUTION_AREAS = [
  'AI Business Solutions',
  'Cloud and AI Platforms',
  'Microsoft Services',
  'Security',
  'Windows and Devices',
] as const

export type SolutionAreaType = (typeof SOLUTION_AREAS)[number]

export const SOLUTION_PLAYS: Record<SolutionAreaType, string[]> = {
  'AI Business Solutions': [
    'AI-ready productivity and security for every employee',
    'AI in the flow of human ambition',
    'Agentify your business processes',
  ],
  'Cloud and AI Platforms': [
    'Amplify your intelligence',
    'Build a unified governed data and AI estate',
    'Modernize with confidence',
    'Ubiquitous Innovation',
  ],
  'Microsoft Services': [
    'Accelerate AI Transformation with Microsoft Services',
  ],
  Security: [
    'AI-ready productivity and security for every employee',
    'Establish a trusted and secure platform for AI',
  ],
  'Windows and Devices': ['AI Transformation with Surface and Copilot'],
} as const

export const HELP_TYPE_OPTIONS = [
  'No help required at this time',
  'Workload - specific value proposition',
  'Customer technical architecture',
  'Proof of concept / Demo',
  'Quotes / Licensing',
  'Post - sales customer success',
  'Adoption and change management',
] as const

export type HelpTypeOption = (typeof HELP_TYPE_OPTIONS)[number]

export const LOCATION_OPTIONS = ['Entire Organization'] as const

export const LOCATION_DEFAULT = 'Entire Organization' as const

/**
 * Microsoft Partner Center co-sell country/region ISO codes.
 * Source: https://learn.microsoft.com/en-us/partner-center/referrals/commercial-marketplace-co-sell-location-codes#country-and-region-codes
 */
export const AZURE_COUNTRY_OPTIONS = {
  OOO: 'Global contacts',
  AF: 'Afghanistan',
  AX: 'Aland Islands',
  AL: 'Albania',
  DZ: 'Algeria',
  AS: 'American Samoa',
  AD: 'Andorra',
  AO: 'Angola',
  AQ: 'Antarctica',
  AG: 'Antigua and Barbuda',
  AR: 'Argentina',
  AM: 'Armenia',
  AW: 'Aruba',
  AU: 'Australia',
  AT: 'Austria',
  AZ: 'Azerbaijan',
  BS: 'Bahamas',
  BH: 'Bahrain',
  BD: 'Bangladesh',
  BB: 'Barbados',
  BY: 'Belarus',
  BE: 'Belgium',
  BZ: 'Belize',
  BJ: 'Benin',
  BM: 'Bermuda',
  BT: 'Bhutan',
  BO: 'Bolivia',
  BQ: 'Bonaire',
  BA: 'Bosnia and Herzegovina',
  BW: 'Botswana',
  BV: 'Bouvet Island',
  BR: 'Brazil',
  IO: 'British Indian Ocean Territory',
  VG: 'British Virgin Islands',
  BN: 'Brunei',
  BG: 'Bulgaria',
  BF: 'Burkina Faso',
  BI: 'Burundi',
  CV: 'Cabo Verde',
  KH: 'Cambodia',
  CM: 'Cameroon',
  CA: 'Canada',
  KY: 'Cayman Islands',
  CF: 'Central African Republic',
  TD: 'Chad',
  CZ: 'Czechia',
  CL: 'Chile',
  CN: 'China',
  CX: 'Christmas Island',
  CC: 'Cocos (Keeling) Islands',
  CO: 'Colombia',
  KM: 'Comoros',
  CG: 'Congo',
  CD: 'Congo (DRC)',
  CK: 'Cook Islands',
  CR: 'Costa Rica',
  CI: "Côte d'Ivoire",
  HR: 'Croatia',
  CU: 'Cuba',
  CW: 'Curaçao',
  CY: 'Cyprus',
  DK: 'Denmark',
  DJ: 'Djibouti',
  DM: 'Dominica',
  DO: 'Dominican Republic',
  EC: 'Ecuador',
  EG: 'Egypt',
  SV: 'El Salvador',
  GQ: 'Equatorial Guinea',
  ER: 'Eritrea',
  EE: 'Estonia',
  SZ: 'eSwatini',
  ET: 'Ethiopia',
  FO: 'Faroe Islands',
  FJ: 'Fiji',
  FI: 'Finland',
  FR: 'France',
  GF: 'French Guiana',
  PF: 'French Polynesia',
  TF: 'French Southern Territories',
  GA: 'Gabon',
  GM: 'Gambia',
  GE: 'Georgia',
  DE: 'Germany',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GR: 'Greece',
  GL: 'Greenland',
  GD: 'Grenada',
  GP: 'Guadeloupe',
  GU: 'Guam',
  GT: 'Guatemala',
  GG: 'Guernsey',
  GN: 'Guinea',
  GW: 'Guinea-Bissau',
  GY: 'Guyana',
  HT: 'Haiti',
  HM: 'Heard Island and McDonald Islands',
  HN: 'Honduras',
  HK: 'Hong Kong SAR',
  HU: 'Hungary',
  IS: 'Iceland',
  IN: 'India',
  ID: 'Indonesia',
  IR: 'Iran',
  IQ: 'Iraq',
  IE: 'Ireland',
  IM: 'Isle of Man',
  IL: 'Israel',
  IT: 'Italy',
  JM: 'Jamaica',
  JP: 'Japan',
  JE: 'Jersey',
  JO: 'Jordan',
  KZ: 'Kazakhstan',
  KE: 'Kenya',
  KI: 'Kiribati',
  KR: 'Korea (South)',
  KW: 'Kuwait',
  KG: 'Kyrgyzstan',
  LA: 'Laos',
  LV: 'Latvia',
  LB: 'Lebanon',
  LS: 'Lesotho',
  LR: 'Liberia',
  LY: 'Libya',
  LI: 'Liechtenstein',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MO: 'Macao SAR',
  MG: 'Madagascar',
  MW: 'Malawi',
  MY: 'Malaysia',
  MV: 'Maldives',
  ML: 'Mali',
  MT: 'Malta',
  MH: 'Marshall Islands',
  MQ: 'Martinique',
  MR: 'Mauritania',
  MU: 'Mauritius',
  YT: 'Mayotte',
  MX: 'Mexico',
  FM: 'Micronesia',
  MD: 'Moldova',
  MC: 'Monaco',
  MN: 'Mongolia',
  ME: 'Montenegro',
  MS: 'Montserrat',
  MA: 'Morocco',
  MZ: 'Mozambique',
  MM: 'Myanmar',
  NA: 'Namibia',
  NR: 'Nauru',
  NP: 'Nepal',
  NL: 'Netherlands',
  NC: 'New Caledonia',
  NZ: 'New Zealand',
  NI: 'Nicaragua',
  NE: 'Niger',
  NG: 'Nigeria',
  NU: 'Niue',
  NF: 'Norfolk Island',
  KP: 'North Korea',
  MP: 'Northern Mariana Islands',
  MK: 'North Macedonia',
  NO: 'Norway',
  OM: 'Oman',
  PK: 'Pakistan',
  PW: 'Palau',
  PS: 'Palestinian Authority',
  PA: 'Panama',
  PG: 'Papua New Guinea',
  PY: 'Paraguay',
  PE: 'Peru',
  PH: 'Philippines',
  PN: 'Pitcairn Islands',
  PL: 'Poland',
  PT: 'Portugal',
  PR: 'Puerto Rico',
  QA: 'Qatar',
  RE: 'Réunion',
  RO: 'Romania',
  RU: 'Russia',
  RW: 'Rwanda',
  BL: 'Saint Barthélemy',
  KN: 'Saint Kitts and Nevis',
  LC: 'Saint Lucia',
  MF: 'Saint Martin',
  PM: 'Saint Pierre and Miquelon',
  VC: 'Saint Vincent and the Grenadines',
  WS: 'Samoa',
  SM: 'San Marino',
  ST: 'São Tomé and Príncipe',
  SA: 'Saudi Arabia',
  SN: 'Senegal',
  RS: 'Serbia',
  SC: 'Seychelles',
  SL: 'Sierra Leone',
  SG: 'Singapore',
  SX: 'Sint Maarten',
  SK: 'Slovakia',
  SI: 'Slovenia',
  SB: 'Solomon Islands',
  SO: 'Somalia',
  ZA: 'South Africa',
  GS: 'South Georgia and South Sandwich Islands',
  SS: 'South Sudan',
  ES: 'Spain',
  LK: 'Sri Lanka',
  SH: 'St Helena, Ascension, Tristan da Cunha',
  SD: 'Sudan',
  SR: 'Suriname',
  SJ: 'Svalbard',
  SE: 'Sweden',
  CH: 'Switzerland',
  SY: 'Syria',
  TW: 'Taiwan',
  TJ: 'Tajikistan',
  TZ: 'Tanzania',
  TH: 'Thailand',
  TL: 'Timor-Leste',
  TG: 'Togo',
  TK: 'Tokelau',
  TO: 'Tonga',
  TT: 'Trinidad and Tobago',
  TN: 'Tunisia',
  TR: 'Türkiye',
  TM: 'Turkmenistan',
  TC: 'Turks and Caicos Islands',
  TV: 'Tuvalu',
  UG: 'Uganda',
  UA: 'Ukraine',
  AE: 'United Arab Emirates',
  GB: 'United Kingdom',
  US: 'United States',
  UY: 'Uruguay',
  UM: 'U.S. Outlying Islands',
  VI: 'U.S. Virgin Islands',
  UZ: 'Uzbekistan',
  VU: 'Vanuatu',
  VA: 'Vatican City',
  VE: 'Venezuela',
  VN: 'Vietnam',
  WF: 'Wallis and Futuna',
  YE: 'Yemen',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
} as const

export type AzureCountryCode = keyof typeof AZURE_COUNTRY_OPTIONS

/** Display name (lowercase) → ISO code from AZURE_COUNTRY_OPTIONS */
const AZURE_COUNTRY_NAME_TO_CODE: Record<string, AzureCountryCode> =
  Object.fromEntries(
    Object.entries(AZURE_COUNTRY_OPTIONS).map(([code, name]) => [
      name.toLowerCase(),
      code as AzureCountryCode,
    ])
  ) as Record<string, AzureCountryCode>

/** Common CRM / free-text aliases → ISO-3166-1 alpha-2 (or MPC OOO) */
const AZURE_COUNTRY_ALIASES: Record<string, AzureCountryCode> = {
  usa: 'US',
  'u.s.': 'US',
  'u.s.a': 'US',
  'u.s.a.': 'US',
  'united states of america': 'US',
  uk: 'GB',
  'u.k.': 'GB',
  'great britain': 'GB',
  britain: 'GB',
  'south korea': 'KR',
  'korea, republic of': 'KR',
  'republic of korea': 'KR',
  russia: 'RU',
  'russian federation': 'RU',
  vietnam: 'VN',
  'viet nam': 'VN',
  syria: 'SY',
  iran: 'IR',
  moldova: 'MD',
  bolivia: 'BO',
  venezuela: 'VE',
  tanzania: 'TZ',
  'czech republic': 'CZ',
  slovakia: 'SK',
  brunei: 'BN',
  'ivory coast': 'CI',
  "cote d'ivoire": 'CI',
  "côte d'ivoire": 'CI',
  laos: 'LA',
  macau: 'MO',
  macao: 'MO',
  palestine: 'PS',
  'global contacts': 'OOO',
  global: 'OOO',
}

/**
 * Normalize a country value to a Partner Center country code from
 * AZURE_COUNTRY_OPTIONS (ISO-3166-1 alpha-2, plus MPC `OOO`).
 * Preserves already-valid codes; maps display names and common aliases.
 * Returns undefined when the value cannot be normalized.
 */
export const normalizeAzureCountryToIso2 = (
  country?: string | null
): AzureCountryCode | undefined => {
  if (country === undefined || country === null) return undefined
  const trimmed = String(country).trim()
  if (!trimmed) return undefined

  const upper = trimmed.toUpperCase()
  if (Object.prototype.hasOwnProperty.call(AZURE_COUNTRY_OPTIONS, upper)) {
    return upper as AzureCountryCode
  }

  const lower = trimmed.toLowerCase()
  return AZURE_COUNTRY_NAME_TO_CODE[lower] ?? AZURE_COUNTRY_ALIASES[lower]
}

export const isAzureCountryCode = (country?: string | null): boolean =>
  Boolean(normalizeAzureCountryToIso2(country))

/**
 * ISO 4217 Currency codes, names, and symbols
 * Source: https://learn.microsoft.com/en-us/dotnet/api/system.globalization.regioninfo.isocurrencysymbol
 * and https://learn.microsoft.com/en-us/dotnet/api/system.globalization.regioninfo.currencysymbol
 */
export const CURRENCY_OPTIONS = {
  USD: { name: 'US Dollar', symbol: '$' },
  EUR: { name: 'Euro', symbol: '€' },
  GBP: { name: 'UK Pound Sterling', symbol: '£' },
  JPY: { name: 'Japanese Yen', symbol: '¥' },
  INR: { name: 'Indian Rupee', symbol: '₹' },
  AUD: { name: 'Australian Dollar', symbol: 'A$' },
  CAD: { name: 'Canadian Dollar', symbol: 'C$' },
  CHF: { name: 'Swiss Franc', symbol: 'CHF' },
  CNY: { name: 'PRC Renminbi', symbol: '¥' },
  SEK: { name: 'Swedish Krona', symbol: 'kr' },
  NOK: { name: 'Norwegian Krone', symbol: 'kr' },
  DKK: { name: 'Danish Krone', symbol: 'kr' },
  CZK: { name: 'Czech Koruna', symbol: 'Kč' },
  PLN: { name: 'Polish Zloty', symbol: 'zł' },
  RUB: { name: 'Russian Ruble', symbol: '₽' },
  ZAR: { name: 'South African Rand', symbol: 'R' },
  BRL: { name: 'Real', symbol: 'R$' },
  MXN: { name: 'Mexican Peso', symbol: '$' },
  ARS: { name: 'Argentine Peso', symbol: '$' },
  NZD: { name: 'New Zealand Dollar', symbol: 'NZ$' },
  HKD: { name: 'Hong Kong Dollar', symbol: 'HK$' },
  SGD: { name: 'Singapore Dollar', symbol: 'S$' },
  KRW: { name: 'Korean Won', symbol: '₩' },
  THB: { name: 'Thai Baht', symbol: '฿' },
  MYR: { name: 'Malaysian Ringgit', symbol: 'RM' },
  PHP: { name: 'Philippine Peso', symbol: '₱' },
  IDR: { name: 'Indonesian Rupiah', symbol: 'Rp' },
  VND: { name: 'Vietnamese Dong', symbol: '₫' },
  HUF: { name: 'Hungarian Forint', symbol: 'Ft' },
  RON: { name: 'Romanian Leu', symbol: 'lei' },
  TRY: { name: 'Turkish Lira', symbol: '₺' },
  AED: { name: 'UAE Dirham', symbol: 'د.إ' },
  SAR: { name: 'Saudi Riyal', symbol: 'ر.س' },
  QAR: { name: 'Qatari Rial', symbol: 'ر.ق' },
  KWD: { name: 'Kuwaiti Dinar', symbol: 'د.ك' },
  BHD: { name: 'Bahraini Dinar', symbol: 'د.ب' },
  ALL: { name: 'Albanian Lek', symbol: 'L' },
  DZD: { name: 'Algerian Dinar', symbol: 'د.ج' },
  AMD: { name: 'Armenian Dram', symbol: '֏' },
  AZN: { name: 'Azerbaijanian Manat', symbol: '₼' },
  BYR: { name: 'Belarussian Ruble', symbol: 'Br' },
  BZD: { name: 'Belize Dollar', symbol: 'BZ$' },
  VEF: { name: 'Venezuelan Bolivar', symbol: 'Bs.' },
  BOB: { name: 'Boliviano', symbol: 'Bs.' },
  BND: { name: 'Brunei Dollar', symbol: 'B$' },
  CLP: { name: 'Chilean Peso', symbol: '$' },
  COP: { name: 'Colombian Peso', symbol: '$' },
  CRC: { name: 'Costa Rican Colon', symbol: '₡' },
  HRK: { name: 'Croatian Kuna', symbol: 'kn' },
  DOP: { name: 'Dominican Peso', symbol: 'RD$' },
  EGP: { name: 'Egyptian Pound', symbol: '£' },
  EEK: { name: 'Estonian Kroon', symbol: 'kr' },
  ETB: { name: 'Ethiopian Birr', symbol: 'Br' },
  GEL: { name: 'Lari', symbol: '₾' },
  GTQ: { name: 'Guatemalan Quetzal', symbol: 'Q' },
  HNL: { name: 'Honduran Lempira', symbol: 'L' },
  ISK: { name: 'Icelandic Krona', symbol: 'kr' },
  IRR: { name: 'Iranian Rial', symbol: '﷼' },
  IQD: { name: 'Iraqi Dinar', symbol: 'ع.د' },
  PKR: { name: 'Pakistan Rupee', symbol: '₨' },
  ILS: { name: 'Israeli New Shekel', symbol: '₪' },
  JMD: { name: 'Jamaican Dollar', symbol: 'J$' },
  JOD: { name: 'Jordanian Dinar', symbol: 'د.ا' },
  KZT: { name: 'Tenge', symbol: '₸' },
  KES: { name: 'Kenyan Shilling', symbol: 'KSh' },
  KGS: { name: 'Kyrgyzstan Som', symbol: 'с' },
  LVL: { name: 'Latvian Lats', symbol: 'Ls' },
  LBP: { name: 'Lebanese Pound', symbol: '£' },
  LYD: { name: 'Libyan Dinar', symbol: 'ل.د' },
  LTL: { name: 'Lithuanian Litas', symbol: 'Lt' },
  MOP: { name: 'Macao Pataca', symbol: 'P' },
  MKD: { name: 'Macedonian Denar', symbol: 'ден' },
  MVR: { name: 'Rufiyaa', symbol: 'Rf' },
  MNT: { name: 'Tugrik', symbol: '₮' },
  MAD: { name: 'Moroccan Dirham', symbol: 'د.م.' },
  NIO: { name: 'Nicaraguan Cordoba Oro', symbol: 'C$' },
  OMR: { name: 'Omani Rial', symbol: 'ر.ع.' },
  PAB: { name: 'Panamanian Balboa', symbol: 'B/.' },
  PYG: { name: 'Paraguay Guarani', symbol: '₲' },
  PEN: { name: 'Peruvian Nuevo Sol', symbol: 'S/' },
  RSD: { name: 'Serbian Dinar', symbol: 'Дин.' },
  SYP: { name: 'Syrian Pound', symbol: '£' },
  TWD: { name: 'New Taiwan Dollar', symbol: 'NT$' },
  TND: { name: 'Tunisian Dinar', symbol: 'د.ت' },
  UAH: { name: 'Ukrainian Hryvnia', symbol: '₴' },
  UYU: { name: 'Peso Uruguayo', symbol: '$U' },
  UZS: { name: 'Uzbekistan Som', symbol: 'сўм' },
  YER: { name: 'Yemeni Rial', symbol: '﷼' },
  ZWL: { name: 'Zimbabwe Dollar', symbol: 'Z$' },
} as const

export const PARTNER_ROLE_OPTIONS = [
  'Adoption and change management',
  'Business strategy',
  'Deployment services',
  'Managed services',
  'Presales envisioning',
  'Proof of concept',
  'Solution design',
  'Transaction',
] as const

export const DEAL_TYPE_OPTIONS = AZURE_DEAL_TYPE_OPTIONS

export const DEAL_TYPE_OPTION_TOOLTIPS: Record<
  (typeof DEAL_TYPE_OPTIONS)[number],
  string
> = {
  'IP Co-sell':
    'Deals that are shared with Microsoft sellers for co-selling using an IP solution.',
  'Services Co-sell':
    'Deals that are for services opportunities such as deployment, managed services etc.',
  Private: 'Deals that are not shared with Microsoft sellers.',
  'Partner-led':
    'Partner owns the deal but shares it with Microsoft for visibility, without asking for help.',
}

/**
 * Get solution play options for a given solution area
 * Returns empty array if solution area not found
 */
export const getSolutionPlaysForArea = (
  solutionArea: SolutionAreaType | string | undefined
): string[] => {
  if (!solutionArea || !SOLUTION_PLAYS.hasOwnProperty(solutionArea)) {
    return []
  }
  return SOLUTION_PLAYS[solutionArea as SolutionAreaType]
}

/**
 * Get all currency codes
 */
export const getCurrencyCodes = (): Array<keyof typeof CURRENCY_OPTIONS> => {
  return Object.keys(CURRENCY_OPTIONS) as Array<keyof typeof CURRENCY_OPTIONS>
}

/**
 * Get currency name by code
 */
export const getCurrencyName = (
  code: keyof typeof CURRENCY_OPTIONS | string
): string => {
  const currency = CURRENCY_OPTIONS[code as keyof typeof CURRENCY_OPTIONS]
  return currency ? currency.name : code
}

/**
 * Get currency symbol by code
 */
export const getCurrencySymbol = (
  code: keyof typeof CURRENCY_OPTIONS | string
): string => {
  const currency = CURRENCY_OPTIONS[code as keyof typeof CURRENCY_OPTIONS]
  return currency ? currency.symbol : code
}

/**
 * Get formatted currency label (Code - Name - Symbol)
 */
export const getCurrencyLabel = (
  code: keyof typeof CURRENCY_OPTIONS | string
): string => {
  const currency = CURRENCY_OPTIONS[code as keyof typeof CURRENCY_OPTIONS]
  return currency ? `${code} - ${currency.name} (${currency.symbol})` : code
}

export const getAzureCountryCodes = (): AzureCountryCode[] => {
  return Object.keys(AZURE_COUNTRY_OPTIONS) as AzureCountryCode[]
}

export const getAzureCountryName = (code: string): string => {
  return AZURE_COUNTRY_OPTIONS[code as AzureCountryCode] ?? code
}

export const getAzureCountrySelectOptions = (): SelectorOptions[] => {
  return getAzureCountryCodes().map(code => ({
    label: AZURE_COUNTRY_OPTIONS[code],
    value: code,
  }))
}

export const getAzureCountryPicklist = (): Array<{
  value: string
  label: string
}> => {
  return getAzureCountrySelectOptions()
}

export const isAzureCountryMappingField = (field: {
  referralField?: string
  key?: string
  label?: string
}): boolean => {
  const normalizedLabel = (field.label || '').trim().toLowerCase()
  return (
    field.referralField === 'country' ||
    field.key === 'country' ||
    normalizedLabel === 'country'
  )
}

export const resolveAzureCountryPicklist = (
  field: {
    referralField?: string
    key?: string
    label?: string
    picklist?: Array<{ value: string; label: string }> | null
  },
  options: { useAzureCountryPicklist: boolean }
): Array<{ value: string; label: string }> => {
  if (options.useAzureCountryPicklist && isAzureCountryMappingField(field)) {
    return getAzureCountryPicklist()
  }

  return field.picklist || []
}

export type AzureReferralStatus = ReferralStatus
export type AzureReferralSubstatus = ReferralSubstatus

export const AZURE_REFERRAL_STATUSES = Object.values(
  AZURE_REFERRAL_STATUS
) as readonly AzureReferralStatus[]

export const AZURE_REFERRAL_SUBSTATUSES = Object.values(
  AZURE_REFERRAL_SUBSTATUS
) as readonly AzureReferralSubstatus[]

export const CLOSED_SUBSTATUSES = TERMINAL_AZURE_REFERRAL_SUBSTATUSES

type StatusChipColors = {
  bgColor: StatusChipBg
  textColor: StatusChipText
  borderColor: StatusChipBg
}

const statusChipColors: Record<AzureReferralStatus, StatusChipColors> = {
  [AZURE_REFERRAL_STATUS.NEW]: {
    bgColor: StatusChipBg.LIGHTBLUE,
    textColor: StatusChipText.LIGHTBLUE,
    borderColor: StatusChipBg.LIGHTBLUE,
  },
  [AZURE_REFERRAL_STATUS.ACTIVE]: {
    bgColor: StatusChipBg.GREEN,
    textColor: StatusChipText.GREEN,
    borderColor: StatusChipBg.GREEN,
  },
  [AZURE_REFERRAL_STATUS.CLOSED]: {
    bgColor: StatusChipBg.GRAY,
    textColor: StatusChipText.GRAY,
    borderColor: StatusChipBg.GRAY,
  },
}

const substatusChipColors: Record<AzureReferralSubstatus, StatusChipColors> = {
  [AZURE_REFERRAL_SUBSTATUS.RECEIVED]: {
    bgColor: StatusChipBg.LIGHTBLUE,
    textColor: StatusChipText.LIGHTBLUE,
    borderColor: StatusChipBg.LIGHTBLUE,
  },
  [AZURE_REFERRAL_SUBSTATUS.PENDING]: {
    bgColor: StatusChipBg.ORANGE,
    textColor: StatusChipText.ORANGE,
    borderColor: StatusChipBg.ORANGE,
  },
  [AZURE_REFERRAL_SUBSTATUS.ACCEPTED]: {
    bgColor: StatusChipBg.GREEN,
    textColor: StatusChipText.GREEN,
    borderColor: StatusChipBg.GREEN,
  },
  [AZURE_REFERRAL_SUBSTATUS.WON]: {
    bgColor: StatusChipBg.GREEN,
    textColor: StatusChipText.GREEN,
    borderColor: StatusChipBg.GREEN,
  },
  [AZURE_REFERRAL_SUBSTATUS.LOST]: {
    bgColor: StatusChipBg.RED,
    textColor: StatusChipText.RED,
    borderColor: StatusChipBg.RED,
  },
  [AZURE_REFERRAL_SUBSTATUS.DECLINED]: {
    bgColor: StatusChipBg.RED,
    textColor: StatusChipText.RED,
    borderColor: StatusChipBg.RED,
  },
  [AZURE_REFERRAL_SUBSTATUS.ERROR]: {
    bgColor: StatusChipBg.RED,
    textColor: StatusChipText.RED,
    borderColor: StatusChipBg.RED,
  },
  [AZURE_REFERRAL_SUBSTATUS.EXPIRED]: {
    bgColor: StatusChipBg.GRAY,
    textColor: StatusChipText.GRAY,
    borderColor: StatusChipBg.GRAY,
  },
}

const toTitleCase = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()

export const formatAzureReferralStatus = (
  status?: string
): AzureReferralStatus | undefined => {
  const match = AZURE_REFERRAL_STATUSES.find(option =>
    matchesReferralStatus(status, option)
  )
  return match
}

export const formatAzureReferralSubstatus = (
  substatus?: string
): AzureReferralSubstatus | undefined => {
  const match = AZURE_REFERRAL_SUBSTATUSES.find(option =>
    matchesReferralSubstatus(substatus, option)
  )
  return match
}

export const getAzureReferralStatusChipColors = (
  status?: string
): StatusChipColors | undefined => {
  const formattedStatus = formatAzureReferralStatus(status)
  return formattedStatus ? statusChipColors[formattedStatus] : undefined
}

export const getAzureReferralSubstatusChipColors = (
  substatus?: string
): StatusChipColors | undefined => {
  const formattedSubstatus = formatAzureReferralSubstatus(substatus)
  return formattedSubstatus
    ? substatusChipColors[formattedSubstatus]
    : undefined
}

export const isClosedAzureReferralSubstatus = (substatus?: string) => {
  const formattedSubstatus = formatAzureReferralSubstatus(substatus)
  return formattedSubstatus
    ? CLOSED_SUBSTATUSES.includes(formattedSubstatus)
    : false
}

export const canEditAzureReferralStatusFields = (
  status?: string,
  substatus?: string
) => {
  return (
    matchesReferralStatus(status, AZURE_REFERRAL_STATUS.ACTIVE) &&
    matchesReferralSubstatus(substatus, AZURE_REFERRAL_SUBSTATUS.ACCEPTED)
  )
}

export const getSelectableAzureReferralStatuses = (
  status?: string
): AzureReferralStatus[] => {
  const formattedStatus = formatAzureReferralStatus(status)
  if (formattedStatus === AZURE_REFERRAL_STATUS.ACTIVE) {
    return [AZURE_REFERRAL_STATUS.ACTIVE, AZURE_REFERRAL_STATUS.CLOSED]
  }
  if (formattedStatus) {
    return [formattedStatus]
  }
  return []
}

export const getSelectableAzureReferralSubstatuses = (
  status?: string
): AzureReferralSubstatus[] => {
  const formattedStatus = formatAzureReferralStatus(status)
  if (formattedStatus === AZURE_REFERRAL_STATUS.NEW) {
    return [AZURE_REFERRAL_SUBSTATUS.RECEIVED, AZURE_REFERRAL_SUBSTATUS.PENDING]
  }
  if (formattedStatus === AZURE_REFERRAL_STATUS.ACTIVE) {
    return [AZURE_REFERRAL_SUBSTATUS.ACCEPTED, ...CLOSED_SUBSTATUSES]
  }
  if (formattedStatus === AZURE_REFERRAL_STATUS.CLOSED) {
    return [...CLOSED_SUBSTATUSES]
  }
  return []
}

export const resolveAzureReferralStatusChange = ({
  nextStatus,
  currentSubstatus,
}: {
  nextStatus: AzureReferralStatus
  currentSubstatus?: string
}): { status: AzureReferralStatus; substatus: AzureReferralSubstatus } => {
  if (nextStatus === AZURE_REFERRAL_STATUS.CLOSED) {
    const closedSubstatus =
      formatAzureReferralSubstatus(currentSubstatus) &&
      isClosedAzureReferralSubstatus(currentSubstatus)
        ? formatAzureReferralSubstatus(currentSubstatus)!
        : AZURE_REFERRAL_SUBSTATUS.WON
    return {
      status: AZURE_REFERRAL_STATUS.CLOSED,
      substatus: closedSubstatus,
    }
  }

  return {
    status: nextStatus,
    substatus:
      formatAzureReferralSubstatus(currentSubstatus) ||
      AZURE_REFERRAL_SUBSTATUS.RECEIVED,
  }
}

export const resolveAzureReferralSubstatusChange = ({
  currentStatus,
  nextSubstatus,
}: {
  currentStatus?: string
  nextSubstatus: AzureReferralSubstatus
}): { status: AzureReferralStatus; substatus: AzureReferralSubstatus } => {
  if (isClosedAzureReferralSubstatus(nextSubstatus)) {
    return {
      status: AZURE_REFERRAL_STATUS.CLOSED,
      substatus: nextSubstatus,
    }
  }

  const formattedStatus =
    formatAzureReferralStatus(currentStatus) || AZURE_REFERRAL_STATUS.NEW
  return { status: formattedStatus, substatus: nextSubstatus }
}

export const getAzureReferralStatusFieldLabel = (value?: string) => {
  const formattedStatus = formatAzureReferralStatus(value)
  return formattedStatus || (value ? toTitleCase(value) : '')
}

export const getAzureReferralSubstatusFieldLabel = (value?: string) => {
  const formattedSubstatus = formatAzureReferralSubstatus(value)
  return formattedSubstatus || (value ? toTitleCase(value) : '')
}
